import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#2F6FE4] mb-4">Privacy Policy</h1>
          <p className="text-[#555555] text-sm mb-12">Last Updated: January 2025</p>

          {/* Section 1: Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Introduction</h2>
            <p className="text-[#555555] leading-relaxed">
              The Broker Marketplace respects the privacy of visitors, brokers, and clients who interact with our website and services. This Privacy Policy explains how information is collected, used, and protected when using our website.
            </p>
          </section>

          {/* Section 2: Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-6">Information We Collect</h2>
            
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-[#2F6FE4] mb-3">Personal Information</h3>
              <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company name</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-[#2F6FE4] mb-3">Business Information</h3>
              <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4">
                <li>Business name</li>
                <li>Business contact information</li>
                <li>Business address</li>
                <li>Business credit profile information submitted by brokers</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-[#2F6FE4] mb-3">Broker Submitted Information</h3>
              <p className="text-[#555555] leading-relaxed">
                Information submitted by brokers through the deal submission forms, including client details, service selections, and supporting documents.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-[#2F6FE4] mb-3">Technical Information</h3>
              <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4">
                <li>Website usage data</li>
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device type</li>
              </ul>
            </div>

            <p className="text-[#555555] leading-relaxed">
              This information helps operate the platform and provide services to brokers and clients.
            </p>
          </section>

          {/* Section 3: How We Use Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">How We Use Information</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              Collected information may be used for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4">
              <li>Review broker applications</li>
              <li>Review submitted client deals</li>
              <li>Communicate with brokers regarding submissions</li>
              <li>Process service requests</li>
              <li>Improve website functionality</li>
              <li>Maintain platform security</li>
            </ul>
          </section>

          {/* Section 4: Broker Submitted Client Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Broker Submitted Client Information</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              Brokers may submit client information through the platform.
            </p>
            <div className="bg-[#F2F4F7] p-6 rounded-lg">
              <p className="text-[#555555] leading-relaxed mb-4">
                By submitting client information, brokers confirm that they have permission from the client to share the information required to review and process requested services.
              </p>
              <p className="text-[#555555] leading-relaxed">
                The Broker Marketplace does not sell or distribute submitted client information.
              </p>
            </div>
          </section>

          {/* Section 5: File Uploads and Documents */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">File Uploads and Documents</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              The platform allows document uploads such as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4 mb-4">
              <li>Business registration documents</li>
              <li>Identification documents</li>
              <li>Business credit reports</li>
              <li>Other supporting documentation</li>
            </ul>
            <p className="text-[#555555] leading-relaxed">
              Uploaded documents are used only for reviewing deal submissions.
            </p>
          </section>

          {/* Section 6: Cookies and Website Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Cookies and Website Data</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              The website may use cookies or analytics tools to improve functionality.
            </p>
            <p className="text-[#555555] leading-relaxed mb-4">
              Cookies may collect anonymous information such as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4">
              <li>Pages visited</li>
              <li>Time spent on pages</li>
              <li>Device type</li>
            </ul>
          </section>

          {/* Section 7: Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Data Security</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              Reasonable measures are taken to protect submitted information.
            </p>
            <p className="text-[#555555] leading-relaxed">
              No internet transmission or electronic storage system can be guaranteed to be completely secure.
            </p>
          </section>

          {/* Section 8: Third Party Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Third Party Services</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              The website may use third party tools to operate the platform, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#555555] ml-4 mb-4">
              <li>Website hosting</li>
              <li>Form processing systems</li>
              <li>Email notification systems</li>
            </ul>
            <p className="text-[#555555] leading-relaxed">
              These services may process submitted information as part of operating the platform.
            </p>
          </section>

          {/* Section 9: No Guarantee of Results */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">No Guarantee of Results</h2>
            <div className="bg-[#F2F4F7] p-6 rounded-lg">
              <p className="text-[#555555] leading-relaxed mb-4">
                The Broker Marketplace provides services intended to assist businesses in developing stronger business credit profiles.
              </p>
              <p className="text-[#555555] leading-relaxed mb-4">
                The Broker Marketplace does not guarantee credit score increases, funding approvals, reporting timelines, or financial results.
              </p>
              <p className="text-[#555555] leading-relaxed">
                Credit reporting and lending decisions are determined by third party institutions.
              </p>
            </div>
          </section>

          {/* Section 10: Changes to This Privacy Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Changes to This Privacy Policy</h2>
            <p className="text-[#555555] leading-relaxed">
              This Privacy Policy may be updated from time to time. Changes will be posted on this page with an updated "Last Updated" date. Continued use of the website after changes are posted constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          {/* Section 11: Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F6FE4] mb-4">Contact Information</h2>
            <p className="text-[#555555] leading-relaxed mb-2">
              For questions about this Privacy Policy, please contact:
            </p>
            <div className="bg-[#F2F4F7] p-6 rounded-lg">
              <p className="text-[#2F6FE4] font-semibold mb-2">The Broker Marketplace</p>
              <p className="text-[#555555]">Email: kenwright@thebrokermarketplace.com</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;